import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

export const useCaptcha = () => {
    const [loadingCaptcha, setLoadingCaptcha] = useState(true);
    const [countdown, setCountdown] = useState(0);
    const canvasRef = useRef(null);
    const animationFrameId = useRef(null);
    const imageUrlRef = useRef(null);
    const timerRef = useRef(null);

    const fetchAndAnimateCaptcha = useCallback(async (isAutoRefresh = false) => {
        setLoadingCaptcha(true);

        // 1. Очищаем предыдущую анимацию, URL и таймер
        if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
        if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
        if (timerRef.current) clearInterval(timerRef.current);
        setCountdown(0); // Сбрасываем таймер сразу

        try {
            const response = await axios.get(`/api/captcha/?_=${new Date().getTime()}`, {
                responseType: 'blob'
            });
            
            const newImageUrl = URL.createObjectURL(response.data);
            imageUrlRef.current = newImageUrl;
            
            const img = new Image();

            img.onload = () => {
                const canvas = canvasRef.current;
                if (!canvas) return;

                const frameWidth = 150, frameHeight = 60, frameCount = 30;
                let frameIndex = 0, lastFrameTime = 0;
                const frameDuration = 1000 / 60; 

                canvas.width = frameWidth;
                canvas.height = frameHeight;
                const ctx = canvas.getContext('2d');

                const animate = (timestamp) => {
                    if (!canvasRef.current) return;
                    if (timestamp - lastFrameTime > frameDuration) {
                        lastFrameTime = timestamp;
                        ctx.clearRect(0, 0, frameWidth, frameHeight);
                        ctx.drawImage(img, 0, frameIndex * frameHeight, frameWidth, frameHeight, 0, 0, frameWidth, frameHeight);
                        frameIndex = (frameIndex + 1) % frameCount;
                    }
                    animationFrameId.current = requestAnimationFrame(animate);
                };
                
                animationFrameId.current = requestAnimationFrame(animate);
                setLoadingCaptcha(false);

                // 2. Запускаем новый таймер обратного отсчета
                setCountdown(300); // 5 минут
                timerRef.current = setInterval(() => {
                    setCountdown(prev => {
                        if (prev <= 1) {
                            clearInterval(timerRef.current);
                            // 3. Автоматически обновляем капчу, когда таймер заканчивается
                            toast.info("Код с картинки истек и был обновлен.");
                            fetchAndAnimateCaptcha(true); 
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            };

            img.onerror = () => {
                toast.error("Не удалось загрузить изображение CAPTCHA.");
                setLoadingCaptcha(false);
            };
            
            img.src = newImageUrl;

        } catch (error) {
            const errorMessage = "Сетевая ошибка при загрузке CAPTCHA.";
            if (isAutoRefresh) {
                console.warn("Не удалось автоматически обновить CAPTCHA.");
            } else if (error.response && error.response.status === 429) {
                 toast.warn("Слишком много запросов. Попробуйте через минуту.");
            } else {
                 toast.error(errorMessage);
            }
            setLoadingCaptcha(false);
        }
    }, []); 

    useEffect(() => {
        fetchAndAnimateCaptcha(false);

        return () => {
            if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
            if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [fetchAndAnimateCaptcha]);

    return { loadingCaptcha, canvasRef, fetchCaptcha: fetchAndAnimateCaptcha, countdown };
};